/**
 * callHistoryActions.js
 * Talkroute
 *
 * Created by Vel Genov on 1/9/19
 * Copyright (c) 2018 Talkroute. All right reserved.
 */
import moment from "moment";

import apiServiceInstance from "../services/TalkrouteAPIService";
import uiString from "../constants/uiString";
import { getTopRelatedCalls } from "../services/dataClasses/CallData";
import { CH_RowsPerPage, ICallHistory } from "@models/Callhistory.models";
import { CallHistoryData } from "@services/dataClasses";
import CallHistoryService from "@services/CallHistory.service";
import { IPagination } from "@models/IPagination";
import { callHistoryJSONParser, callHistoryJSONParserV2 } from "@services/dataClasses/CallHistoryData";
import _ from "lodash";
import { addContactNameToCallHistory } from "@helpers/functions/contacts";
import { RootState } from "@helpers/store/store";
import { mergeContactsToCallHistory } from "@reducers/contactsReducer";

/**
 * Constants for the different redux action types
 */
export const FETCH_CALL_HISTORY_BEGIN = "FETCH_CALL_HISTORY_BEGIN";
export const FETCH_CALL_HISTORY_FAILURE = "FETCH_CALL_HISTORY_FAILURE";
export const FETCH_CALL_HISTORY_EMPTY = "FETCH_CALL_HISTORY_EMPTY";
export const SHOW_CALL_DETAILS = "SHOW_CALL_DETAILS";
export const ENTER_CALL_HISTORY = "ENTER_CALL_HISTORY";
export const LEAVE_CALL_HISTORY = "LEAVE_CALL_HISTORY";
export const HIDE_CALL_DETAILS = "HIDE_CALL_DETAILS";
export const CLEAR_MISSED_COUNT = "CLEAR_MISSED_COUNT";
export const APPLY_CALL_HISTORY_FILTER = "APPLY_CALL_HISTORY_FILTER";
export const SET_CALL_HISTORY = "SET_CALL_HISTORY";
export const UPDATE_CALL_HISTORY_ROWS_PER_PAGE_DEFAULT =
  "UPDATE_CALL_HISTORY_ROWS_PER_PAGE_DEFAULT";

export type ISetCallHistoryParams = {
  data: CallHistoryData[];
  pagination?: IPagination;
  callHistoryLastViewed: number;
};

export type IFetchCallHistoryActionOptions = {
  page?: number;
  append?: boolean;
  background?: boolean;
};

/**
 * Gets the call history from a web service
 * @param options.page - page number to get
 * @param options.append - set to true ensures new calls are appended to rather than
 * replace existing calls. Resulting list will not contain two calls with the same id
 *  If set to false - replace all existing calls with the ones from the response
 * @returns
 */
export const fetchCallHistory = ({
  page = 1,
  append = true,
  background,
}: IFetchCallHistoryActionOptions) => {
  return async (dispatch, getState: () => RootState) => {
    const callhistoryService = CallHistoryService();
    // const contactList = getState().contactsReducer.data;
    if (!background) dispatch(fetchCallHistoryBegin());
    try {
      const { data, pagination } = await callhistoryService.GetByPageV1(page);
      let parsedData = callHistoryJSONParser(data);

      // if (contactList.length) {
      //   parsedData = addContactNameToCallHistory(contactList, parsedData);
      // } else {
      //   dispatch(mergeContactsToCallHistory(true));
      // }
      let sortedData: CallHistoryData[];
      if (append) {
        sortedData = _.uniqBy(
          sortCallHistory(parsedData.concat(getState().callHistory.data)),
          "id"
        );
      } else {
        sortedData = sortCallHistory(parsedData);
      }

      const callHistoryLastViewed = getState().authentication.callHistoryLastVisited;
      dispatch(
        fetchCallHistorySuccess({
          data: sortedData,
          pagination,
          callHistoryLastViewed,
        })
      );
      return sortedData;
    } catch (error) {
      dispatch(fetchCallHistoryFailure(error));
    }
  };
};

/**
 * Used to fetch call history by the pooling manager.
 * It will always add any new calls to the existing call list
 * instead of replacing them
 */
export const fetchCallHistoryUpdates = (background?: boolean) => {
  return async (dispatch, getState: () => RootState) => {
    const callhistoryService = CallHistoryService();
    
    try {
      const existItems = getState().callHistory.data;
      // const contactList = getState().contactsReducer.data;
      if (!background) dispatch(fetchCallHistoryBegin());

      const data = await callhistoryService.GetByPageV1(1);
      const { data: newCalls, pagination } = data;
      const lastCall = existItems[0];
      

      if (lastCall && !newCalls.find((call) => call.id === lastCall.id)) {
        for (let page = 2; page < pagination.totalPages; page++) {
          const { data } = await callhistoryService.GetByPageV1(page);
          newCalls.push(...data);
          if (data.find((call) => call.id === lastCall.id)) break;
        }
      }
      
      let parsedNewCalls = callHistoryJSONParser(newCalls)

      // if (contactList.length) {
      //   parsedNewCalls = addContactNameToCallHistory(contactList, parsedNewCalls);
      // } else {
      //   dispatch(mergeContactsToCallHistory(true));
      // }

      const sortedData = _.uniqBy(
        sortCallHistory(parsedNewCalls.concat(existItems)),
        "id"
      );
      const callHistoryLastViewed = getState().authentication.callHistoryLastVisited;

      dispatch(
        fetchCallHistorySuccess({
          data: sortedData,
          pagination,
          callHistoryLastViewed,
        })
      );
    } catch (error) {
      dispatch(fetchCallHistoryFailure(error));
    }
  };
};


export const setCallHistory = ({
  data,
  pagination,
  callHistoryLastViewed,
}: ISetCallHistoryParams) => ({
  type: SET_CALL_HISTORY,
  payload: { data, pagination, callHistoryLastViewed },
});
/**
 * Show details about a call. Also finds up to TOP_CALLS_NUMBER related calls in the call history
 * @param {*} callHistory An array containing call history
 * @param {*} call The call to show details for
 * @param {*} parent the parent component that we are showing call details about. Usually CallHistory
 */
export const showCallDetails = (callHistory: CallHistoryData[], call: CallHistoryData | null, parent: string | null) => ({
  type: SHOW_CALL_DETAILS,
  payload: {
    call,
    topRelatedCalls: getTopRelatedCalls(callHistory, call),
    parent,
  },
});

/**
 * Sets the number of call history rows to fetch in a table
 * @param payload
 */
export const updateRowsPerPageSetting = (payload: CH_RowsPerPage) => ({
  type: UPDATE_CALL_HISTORY_ROWS_PER_PAGE_DEFAULT,
  payload,
});

/**
 * Hide details about a call.
 * @param {*} parent the parent component that we are showing call details about. Usually CallHistory
 */
export const hideCallDetails = (parent) => ({
  type: HIDE_CALL_DETAILS,
  payload: {
    call: null,
    topRelatedCalls: null,
    parent,
  },
});

/**
 * Helper function
 * Filters the passed in call history by a passed in filter.
 * @param {*} callHistoryData An array containing call history
 * @param {*} filterBy A phone number to filter by
 */
export const callHistoryFilterHelper = (callHistoryData, filterBy) => {
  if (filterBy === uiString.ALL_CALLS) {
    return callHistoryData;
  }
  if (Array.isArray(callHistoryData) && callHistoryData.length > 0) {
    // Null or empty check for call history
    const filteredCallHistory = callHistoryData.filter(
      (callRecord) => callRecord.numberDialed === filterBy
    );
    return filteredCallHistory;
  }

  return callHistoryData;
};

/**
 * Helper function
 * Sorts the passed in array of call history
 * @param {*} data the call history to sort
 */
export const sortCallHistory = (data: CallHistoryData[]) => {
  const sortedData = data;
  if (Array.isArray(sortedData) && sortedData.length > 1) {
    // Sort by date before returning
    sortedData.sort(
      (a, b) =>
        // Turn the strings into dates, and then subtract them
        // to get a value that is either negative, positive, or zero.
        b.date.valueOf() - a.date.valueOf()
    );
  }

  return sortedData;
};

/**
 * Start fething call history action
 */
export const fetchCallHistoryBegin = () => ({
  type: FETCH_CALL_HISTORY_BEGIN,
});

/**
 * Successfully fetched call history
 * @param {*} callHistory the call history array that was fetched
 */
export const fetchCallHistorySuccess = setCallHistory;

/**
 * An error occured while fetching call history
 * @param {*} error
 */
export const fetchCallHistoryFailure = (error) => ({
  type: FETCH_CALL_HISTORY_FAILURE,
  payload: { error },
});

/**
 * Count the number of missed calls
 */
export const countMissedCalls = (data: CallHistoryData[], callHistoryLastViewed: number): number => {
  const defaultUnreadCount = 0;

  if (Array.isArray(data) && data.length > 1) {
    // Count the number of missed or hangup calls 
    const unreadCount = data.reduce((accumulator, currentCall) => {
      const callDate = currentCall.date.unix();
      if ((currentCall.callResult === 'missed' || currentCall.callResult === 'hangup') && (callDate > callHistoryLastViewed)) {
        return accumulator + 1;
      }

      return accumulator;
    }, defaultUnreadCount);
    return unreadCount;
  }
  return defaultUnreadCount;
};

export const clearMissedCount = () => {
  return (dispatch) => {
    dispatch({ type: 'CLEAR_MISSED_COUNT' });
  };
};

/**
 * An empty action. Does not change the state
 */
export const fetchCallHistoryEmptyAction = () => ({
  type: FETCH_CALL_HISTORY_EMPTY,
});

/**
 * An action to apply a filter to the passed in call history
 * @param {*} callHistory the call history to filter
 * @param {*} filterBy the phone number to filter by
 */
export const applyCallHistoryFilter = (callHistory, filterBy) => {
  return {
    type: APPLY_CALL_HISTORY_FILTER,
    payload: { filterBy },
  };
};

const callHistoryActions = {
  fetchCallHistory,
  fetchCallHistoryUpdates,
  showCallDetails,
  hideCallDetails,
  callHistoryFilterHelper,
  sortCallHistory,
  fetchCallHistoryBegin,
  fetchCallHistorySuccess,
  fetchCallHistoryFailure,
  countMissedCalls,
  clearMissedCount,
  fetchCallHistoryEmptyAction,
  applyCallHistoryFilter,
};

export default callHistoryActions;
